import DialogProvider from "@components/dialog_provider"
import TextInput from "@components/text_input"
import { oneTimesUpdate } from "@domain/coupons"
import { getCustomers } from "@domain/customer/list"
import useControlledForm from "@hooks/use_controlled_form"
import useTranslate from "@hooks/use_translate"
import { Stack, Typography } from "@mui/material"
import { useQuery } from "@tanstack/react-query"
import { showErrorToast, showSuccessToast } from "@utils/toast"
import { OneTimesType } from "__generated__/graphql"
import { useMemo, useState } from "react"

type ResyncQuickAnswerRagDialogProps = {
  isOpen: boolean
  setIsOpen: (isOpen: boolean) => void
}

export default function ResyncQuickAnswerRagDialog({
  isOpen,
  setIsOpen
}: ResyncQuickAnswerRagDialogProps) {
  const { translate } = useTranslate()
  const [loading, setLoading] = useState(false)
  const { control, reset, watch } = useControlledForm<{ customerId: string }>({
    mode: "all",
    defaultValues: {
      customerId: ""
    }
  })
  const selectedCustomerId = watch("customerId")

  const { data: customerResponse, isLoading: customersLoading } = useQuery({
    queryKey: ["scripts-resync-qa-rag-customers"],
    enabled: isOpen,
    queryFn: async () => {
      const response = await getCustomers({
        pageNumber: 1,
        pageSize: 2000
      })

      if (!response.isSuccess) {
        showErrorToast("Failed to load customers")
        return {
          docs: [],
          totalDocs: 0
        }
      }

      return response.data
    }
  })

  const customerOptions = useMemo(
    () =>
      (customerResponse?.docs ?? [])
        .filter((item) => item.customer?.appStatus === "Enabled")
        .map((item) => ({
          value: item.customer?._id || "",
          label: `${item.customer?.companyName || "Unknown"} (${item.customer?._id || ""})`
        })),
    [customerResponse?.docs]
  )

  const handleClose = () => {
    if (loading) {
      return
    }
    setIsOpen(false)
    reset()
  }

  const onSubmit = async () => {
    setLoading(true)
    const payload = selectedCustomerId ? { customerId: selectedCustomerId } : {}

    const {
      isSuccess,
      errorMessage,
      data: responseData
    } = await oneTimesUpdate({
      type: OneTimesType.ResyncQuickAnswerRag,
      metaData: payload
    })

    if (!isSuccess || !responseData) {
      setLoading(false)
      showErrorToast(String(errorMessage))
      return
    }

    showSuccessToast("Quick Answer RAG re-sync started in background.")
    setLoading(false)
    handleClose()
  }

  return (
    <DialogProvider
      title="Re-sync Quick Answer RAG"
      leftButton={{
        text: translate("cancel"),
        color: "secondary",
        onClick: handleClose,
        disabled: loading
      }}
      rightButton={{
        text: "Start",
        color: "primary",
        onClick: onSubmit,
        loading,
        disabled: loading
      }}
      isOpen={isOpen}
      setClose={handleClose}
      size="small"
    >
      <Stack gap={1}>
        <Typography variant="body2">
          Leave customer empty to run for all Enabled customers.
        </Typography>
        <TextInput<{ customerId: string }>
          type="select"
          label={translate("customer")}
          name="customerId"
          placeholder="All customers"
          control={control}
          disabled={customersLoading || loading}
          selectOptions={[
            { label: "All customers", value: "" },
            ...customerOptions
          ]}
        />
      </Stack>
    </DialogProvider>
  )
}
